import { useState } from "react";
import { Check, PenLine, Send } from "lucide-react";
import { cn } from "@/lib/utils";

const isOther = (o) => /^other\b/i.test(o);
const valueOf = (a) => [...(a?.sel || []), ...(a?.otherOn && a.other?.trim() ? [a.other.trim()] : []), ...(a?.text?.trim() ? [a.text.trim()] : [])];

function compose(questions, answers) {
  const lines = questions.map((q) => {
    const v = valueOf(answers[q.id]);
    return v.length ? `- ${q.question.replace(/\?+$/, "")}: ${v.join(", ")}` : null;
  }).filter(Boolean);
  const skipped = lines.length < questions.length ? "\n- Anything I didn't answer: use your best judgement." : "";
  return `Here are my answers:\n${lines.join("\n")}${skipped}\nPlease go ahead with these.`;
}

function Legend({ q, qi, total }) {
  const hint = q.type === "text" ? "In your words" : q.multi ? "Pick any" : null;
  return (
    <legend className="text-sm font-semibold text-foreground mb-2">
      <span className="hs-mono text-xs text-primary mr-2">{qi + 1}/{total}</span>{q.question}
      {hint && <span className="ml-2 text-xs font-normal text-muted-foreground">{hint}</span>}
    </legend>
  );
}

// Short options read best as chips; long, descriptive ones as full-width rows.
function Option({ label, on, long, multi, onClick, testId, icon }) {
  if (!long) return <button type="button" aria-pressed={on} onClick={onClick} data-testid={testId} className={cn("hs-chip", on && "hs-chip-active")}>{on ? <Check className="h-3.5 w-3.5" /> : icon}{label}</button>;
  return (
    <button type="button" aria-pressed={on} onClick={onClick} data-testid={testId} className={cn("hs-option-row", on && "is-on")}>
      <span className={cn("mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center", multi ? "rounded" : "rounded-full", on ? "bg-primary border-primary text-primary-foreground" : "border-foreground/30")}>{on && <Check className="h-3 w-3" />}</span>
      <span className="min-w-0">{label}</span>
    </button>
  );
}

function ChoiceQuestion({ q, qi, total, a, active, pick, toggleOther, setOther }) {
  const opts = q.options.filter((o) => !isOther(o));
  const long = opts.some((o) => o.length > 32);
  return (
    <fieldset disabled={!active} className="space-y-2 min-w-0" data-testid={`ai-q-${qi}`} data-layout={long ? "rows" : "chips"}>
      <Legend q={q} qi={qi} total={total} />
      <div className={long ? "hs-option-grid" : "flex flex-wrap gap-2"}>
        {opts.map((o, oi) => <Option key={o} label={o} on={(a.sel || []).includes(o)} long={long} multi={q.multi} onClick={() => pick(q, o)} testId={`ai-q-${qi}-opt-${oi}`} />)}
        <Option label="Other" on={!!a.otherOn} long={long} multi={q.multi} onClick={() => toggleOther(q)} testId={`ai-q-${qi}-other`} icon={<PenLine className="h-3.5 w-3.5" />} />
      </div>
      {a.otherOn && (
        <input autoFocus value={a.other || ""} onChange={(e) => setOther(q, e.target.value)} maxLength={300} placeholder="Type your own answer"
          aria-label={`Your answer: ${q.question}`} data-testid={`ai-q-${qi}-other-input`}
          className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50" />
      )}
    </fieldset>
  );
}

function TextQuestion({ q, qi, total, a, active, setText }) {
  return (
    <fieldset disabled={!active} className="space-y-2.5 min-w-0" data-testid={`ai-q-${qi}`} data-layout="text">
      <Legend q={q} qi={qi} total={total} />
      <textarea value={a.text || ""} onChange={(e) => setText(q, e.target.value)} rows={2} maxLength={1000} placeholder={q.placeholder || "Type your answer"}
        aria-label={q.question} data-testid={`ai-q-${qi}-text`}
        className="w-full rounded-lg border border-border bg-background p-3 text-sm leading-6 outline-none focus:border-primary/50 resize-y min-h-[64px]" />
      {q.options?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Start from:</span>
          {q.options.map((o, oi) => <button type="button" key={o} onClick={() => setText(q, o)} data-testid={`ai-q-${qi}-suggest-${oi}`} className="hs-chip text-xs">{o}</button>)}
        </div>
      )}
    </fieldset>
  );
}

export function QuestionCard({ questions, active, onSubmit }) {
  const [answers, setAnswers] = useState({});
  const set = (id, patch) => setAnswers((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
  const pick = (q, opt) => {
    const cur = answers[q.id]?.sel || [];
    if (q.multi) set(q.id, { sel: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] });
    else set(q.id, { sel: cur[0] === opt ? [] : [opt], otherOn: false });
  };
  const toggleOther = (q) => {
    const on = !answers[q.id]?.otherOn;
    set(q.id, q.multi ? { otherOn: on } : { otherOn: on, sel: on ? [] : answers[q.id]?.sel || [] });
  };
  const done = questions.filter((q) => valueOf(answers[q.id]).length).length;
  return (
    <div role="group" aria-label="Questions from your copilot" data-testid="ai-questions"
      className={cn("mt-3 rounded-2xl border bg-card/60 p-4 space-y-4", active ? "border-primary/30" : "border-border opacity-60")}>
      {questions.map((q, qi) => {
        const props = { q, qi, total: questions.length, a: answers[q.id] || {}, active };
        return q.type === "text"
          ? <TextQuestion key={q.id} {...props} setText={(x, v) => set(x.id, { text: v })} />
          : <ChoiceQuestion key={q.id} {...props} pick={pick} toggleOther={toggleOther} setOther={(x, v) => set(x.id, { other: v })} />;
      })}
      {active && (
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground mr-auto" data-testid="ai-questions-count">{done} of {questions.length} answered</span>
          <button type="button" onClick={() => onSubmit("Use your best judgement for all of these and go ahead.")} data-testid="ai-questions-skip" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Skip — use your judgement</button>
          <button type="button" disabled={!done} onClick={() => onSubmit(compose(questions, answers))} data-testid="ai-questions-submit"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 hover:brightness-110 transition-[filter]">
            <Send className="h-3.5 w-3.5" />Send answers
          </button>
        </div>
      )}
    </div>
  );
}
